'use client';

import { MaskType, MaskVariant } from '~/components/primitives';

import Image from 'next/image';
import React from 'react';
import { cx } from 'class-variance-authority';

interface OptimizeImageProps {
    src: string;
    width: number;
    height?: number;
    quality?: number;
}

const storyblokLoader = ({ src, width, height = 0, quality = 75 }: OptimizeImageProps) =>
    `${src}/m/${width}x${height}/smart/filters:quality(${quality})`; // TODO: Add focal point

interface StoryblokMediaProps {
    src: string;
    alt: string;
    poster?: string;
    width?: number | undefined;
    height?: number | undefined;
    className?: string;
    style?: React.CSSProperties;
    controls?: boolean;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    maskType?: MaskType;
    fill?: boolean;
    priority?: boolean;
    sizes?: string;
}

function StoryblokMedia({
    src,
    alt,
    poster,
    width = 0,
    height = 0,
    className = '',
    style = {},
    controls = true,
    autoplay = false,
    loop = false,
    muted = false,
    maskType,
    fill = true,
    priority = false,
    sizes = '100vw',
}: StoryblokMediaProps) {
    const props = {
        ...(fill && { fill }),
        ...(style && { style }),
        ...(priority && { priority }),
    };

    let assetImageOrVideo = '';
    if (src) {
        const extension = src.split('.').pop();
        switch (extension) {
            case 'mp4':
            case 'avi':
            case 'mov':
                assetImageOrVideo = 'video';
                break;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                assetImageOrVideo = 'image';
                break;
            default:
                assetImageOrVideo = 'image';
        }
    }

    if (assetImageOrVideo === 'image') {
        return (
            <Image
                loader={storyblokLoader}
                src={src}
                alt={alt}
                width={width}
                height={height}
                sizes={sizes}
                className={cx(className, maskType && MaskVariant({ mask: maskType }))}
                {...props}
            />
        );
    } else if (assetImageOrVideo === 'video') {
        return (
            <video
                src={src}
                poster={poster}
                width={width}
                height={height}
                className={cx(className, maskType && MaskVariant({ mask: maskType }))}
                controls={controls}
                autoPlay={autoplay}
                loop={loop}
                muted={muted}
                {...props}
            >
                <track kind="captions" />
            </video>
        );
    }

    return null;
}

export default StoryblokMedia;
